const DEFAULT_STORAGE_KEY_PREFIX = 'event-triggered-'

type StoredEvent = {
  timestamp: number
}

/**
 * Universal event deduplication utility for tracking events
 * Prevents the same event from being sent multiple times within a specified time window
 *
 * @param key - Unique identifier for the event (e.g., orderId, userId, etc.)
 * @param maxAgeMinutes - How long to remember that an event was sent (default: 60 minutes)
 * @param prefix - Storage key prefix (default: 'event-triggered-')
 * @returns Object with isAlreadySent and setEventAlreadySent functions
 */
export const eventDeduplication = (
  key: string,
  maxAgeMinutes: number = 60,
  prefix: string = DEFAULT_STORAGE_KEY_PREFIX,
) => {
  const storageKey = prefix + key
  const maxAgeMs = maxAgeMinutes * 60 * 1000

  const isLocalStorageAvailable = (): boolean => {
    return typeof localStorage !== 'undefined'
  }

  const now = () => Date.now()

  const isExpired = (timestamp?: number): boolean => {
    return typeof timestamp !== 'number' || now() - timestamp > maxAgeMs
  }

  const parseStoredEvent = (value: string | null): StoredEvent | null => {
    try {
      return JSON.parse(value ?? '') as StoredEvent
    } catch {
      return null
    }
  }

  const cleanupExpiredEntries = (): void => {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const itemKey = localStorage.key(i)
      if (!itemKey?.startsWith(prefix)) {
        continue
      }
      const value = localStorage.getItem(itemKey)
      const data = parseStoredEvent(value)
      if (!data || isExpired(data.timestamp)) {
        localStorage.removeItem(itemKey)
      }
    }
  }
  const isAlreadySent = (): boolean => {
    if (!isLocalStorageAvailable()) {
      return false
    }
    cleanupExpiredEntries()
    const value = localStorage.getItem(storageKey)
    const data = parseStoredEvent(value)
    if (!data || isExpired(data.timestamp)) {
      localStorage.removeItem(storageKey)
      return false
    }
    return true
  }

  const setEventAlreadySent = (): void => {
    if (!isLocalStorageAvailable()) {
      return
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        timestamp: Date.now(),
      }),
    )
  }

  return {
    isAlreadySent,
    setEventAlreadySent,
  }
}
